import { Link, useLocation } from 'react-router-dom';
import * as api from '../../api/client';
import { useFleetReport } from '../../hooks/useReports';
import { useReportParams } from './ScopeRangePicker';
import { Card } from '../common/Card';
import { Skeleton } from '../common/Skeleton';
import { formatDurationSeconds } from '../../utils/series';
import { GradeBadge } from './GradeBadge';
import { DeltaValue } from './DeltaValue';
import { CoverageNote } from './CoverageNote';
import { EstimateBadge } from './EstimateBadge';

/** Every metric here is nullable. Null renders as an em dash, never as 0 or 100. */
const num = (v: number | null | undefined, digits = 0, suffix = '') =>
  v === null || v === undefined ? '—' : `${v.toFixed(digits)}${suffix}`;

export function FleetReport() {
  const { query } = useReportParams();
  const { data, isLoading, isError } = useFleetReport(query);
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Loading report">
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }
  if (isError || !data) {
    return <Card className="p-10 text-center text-gray-500 dark:text-gray-400">Could not load this report.</Card>;
  }
  if (data.rows.length === 0) {
    return <Card className="p-10 text-center text-gray-500 dark:text-gray-400">No services match this scope.</Card>;
  }

  const meanApprox = data.meta.source.response_mean === 'hourly-approx';
  const p95Estimated = data.meta.source.percentiles === 'hourly-estimate';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <CoverageNote coverage={data.meta.coverage} />
        <a
          href={api.reportCsvUrl('fleet', query)}
          download
          className="no-print text-sm text-brand-600 dark:text-brand-400 hover:underline"
        >
          Export CSV
        </a>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th scope="col" className="text-left px-4 py-3">Service</th>
              <th scope="col" className="text-left px-2 py-3">Grade</th>
              <th scope="col" className="text-right px-2 py-3">Uptime</th>
              <th scope="col" className="text-right px-2 py-3">Incidents</th>
              <th scope="col" className="text-right px-2 py-3">MTTR</th>
              <th scope="col" className="text-right px-2 py-3">
                Mean{meanApprox && <EstimateBadge kind="mean-approx" />}
              </th>
              <th scope="col" className="text-right px-2 py-3">
                p95{p95Estimated && <EstimateBadge kind="percentile-estimate" />}
              </th>
              <th scope="col" className="text-right px-4 py-3">Coverage</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {data.rows.map(r => (
              <tr key={r.service_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                <td className="px-4 py-2.5">
                  <Link
                    to={{ pathname: `/reports/services/${r.service_id}`, search: location.search }}
                    className="text-brand-600 dark:text-brand-400 hover:underline"
                  >
                    {r.service_name}
                  </Link>
                </td>
                <td className="px-2 py-2.5">
                  <GradeBadge grade={r.grade} budgetConsumed={r.error_budget_consumed} />
                </td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums">
                  {num(r.uptime, 3, '%')}
                  {r.uptime_delta !== undefined && (
                    <div><DeltaValue value={r.uptime_delta} unit="pp" /></div>
                  )}
                </td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums">{r.incident_count}</td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums">
                  {formatDurationSeconds(r.mttr_seconds)}
                </td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums">{num(r.mean_response, 0, ' ms')}</td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums">{num(r.p95, 0, ' ms')}</td>
                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-xs text-gray-500 dark:text-gray-400">
                  {r.hours_present}/{r.hours_expected}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
